import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, fmtSDG } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Bike, MapPin, Phone, Banknote, CreditCard, CheckCircle2, Clock, Package, RefreshCcw, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import { playNewOrderChime, playTestBeep, isSoundEnabled, setSoundEnabled, unlockAudio } from "@/lib/notify";

function relativeTime(iso, lang) {
    if (!iso) return "";
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1) return lang === "ar" ? "الآن" : "now";
    if (m < 60) return lang === "ar" ? `قبل ${m} د` : `${m}m ago`;
    const h = Math.floor(m / 60);
    return lang === "ar" ? `قبل ${h} س` : `${h}h ago`;
}

function PaymentBadge({ method, t }) {
    if (!method) return null;
    const isCash = method === "cash";
    return (
        <span
            className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${
                isCash
                    ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40"
                    : "bg-blue-500/15 text-blue-400 border-blue-500/40"
            }`}
            data-testid={`payment-${method}`}
        >
            {isCash ? <Banknote className="h-3 w-3" /> : <CreditCard className="h-3 w-3" />}
            {t(method)}
        </span>
    );
}

function OrderCard({ o, lang, t, onPickup, onDelivered, mode }) {
    const isAr = lang === "ar";
    const total = (o.total || 0) + (o.delivery_fee || 0);
    return (
        <Card className="p-4 border border-border bg-card hover:bg-card/80 transition-all" data-testid={`order-card-${o.id}`}>
            <div className="flex items-start justify-between gap-2 mb-3">
                <div>
                    <div className="text-2xl font-bold text-primary tabular-nums">#{o.order_no}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {relativeTime(o.created_at, lang)}
                    </div>
                </div>
                <div className="text-right">
                    <PaymentBadge method={o.payment_method} t={t} />
                    <div className="text-lg font-bold tabular-nums mt-1">{fmtSDG(total, lang)}</div>
                </div>
            </div>

            <div className="space-y-2 mb-3">
                <div className="text-sm font-medium">{o.customer_name}</div>
                <a href={`tel:${o.customer_phone}`} className="flex items-center gap-2 text-sm text-primary hover:underline" data-testid="call-customer">
                    <Phone className="h-4 w-4" />
                    <span dir="ltr" className="tabular-nums">{o.customer_phone}</span>
                </a>
                {o.delivery_address && (
                    <div className="flex items-start gap-2 text-sm text-muted-foreground bg-secondary/40 rounded-lg p-2">
                        <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5 text-primary" />
                        <span className="break-words">{o.delivery_address}</span>
                    </div>
                )}
            </div>

            <div className="space-y-1 mb-3 border-t border-border pt-2">
                {(o.items || []).map((it, i) => (
                    <div key={i} className="flex justify-between text-xs">
                        <span className="text-muted-foreground">{it.qty}× {isAr ? it.name_ar : it.name_en}</span>
                        <span className="tabular-nums">{fmtSDG((it.price || 0) * (it.qty || 1), lang)}</span>
                    </div>
                ))}
                {o.delivery_fee > 0 && (
                    <div className="flex justify-between text-xs text-amber-400">
                        <span>{t("delivery_fee")}</span>
                        <span className="tabular-nums">{fmtSDG(o.delivery_fee, lang)}</span>
                    </div>
                )}
            </div>

            {mode === "available" ? (
                <Button
                    onClick={() => onPickup(o)}
                    className="w-full h-12 text-base gap-2 bg-primary hover:bg-primary/90"
                    data-testid={`pickup-btn-${o.id}`}
                >
                    <Package className="h-5 w-5" /> {t("pick_up")}
                </Button>
            ) : o.status === "out_for_delivery" ? (
                <Button
                    onClick={() => onDelivered(o)}
                    className="w-full h-12 text-base gap-2 bg-emerald-600 hover:bg-emerald-700"
                    data-testid={`delivered-btn-${o.id}`}
                >
                    <CheckCircle2 className="h-5 w-5" /> {t("mark_delivered")}
                </Button>
            ) : (
                <div className="text-center text-sm text-emerald-400 py-2 flex items-center justify-center gap-1">
                    <CheckCircle2 className="h-4 w-4" /> {t("delivered_at")} {relativeTime(o.delivered_at || o.closed_at, lang)}
                </div>
            )}
        </Card>
    );
}

export default function DriverHome() {
    const { user, lang, t, setUser } = useApp();
    const [tab, setTab] = useState("available");
    const [available, setAvailable] = useState([]);
    const [mine, setMine] = useState([]);
    const [loading, setLoading] = useState(false);
    const [soundOn, setSoundOnState] = useState(isSoundEnabled());
    const [audioUnlocked, setAudioUnlocked] = useState(false);
    const prevOrderIdsRef = useRef(new Set());
    const isAr = lang === "ar";

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [a, m] = await Promise.all([
                api.get("/driver/queue"),
                api.get("/driver/my-orders"),
            ]);
            const newAvailable = a.data || [];
            // Detect newly arrived orders (id not seen in previous tick)
            const prev = prevOrderIdsRef.current;
            const newIds = newAvailable.map((o) => o.id);
            const justArrived = newIds.filter((id) => !prev.has(id));
            // Skip ringing on the very first load (when prev is empty)
            if (prev.size > 0 && justArrived.length > 0 && isSoundEnabled()) {
                playNewOrderChime(2);
            }
            prevOrderIdsRef.current = new Set(newIds);
            setAvailable(newAvailable);
            setMine(m.data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || t("error"));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        load();
        const id = setInterval(load, 15000); // auto-refresh every 15s
        return () => clearInterval(id);
    }, [load]);

    // Unlock audio context on first user interaction (mobile autoplay policy)
    const handleUnlockAudio = () => {
        unlockAudio();
        playTestBeep();
        setAudioUnlocked(true);
    };

    const toggleSound = () => {
        const next = !soundOn;
        setSoundEnabled(next);
        setSoundOnState(next);
        if (next) {
            unlockAudio();
            playTestBeep();
            setAudioUnlocked(true);
        }
    };

    const handlePickup = async (o) => {
        if (!window.confirm(`${t("confirm_pickup")} #${o.order_no}?`)) return;
        try {
            await api.post(`/orders/${o.id}/pickup`);
            toast.success(t("out_for_delivery"));
            setTab("mine");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || t("error"));
        }
    };

    const handleDelivered = async (o) => {
        if (!window.confirm(`${t("confirm_delivered_q")} #${o.order_no}`)) return;
        try {
            await api.post(`/orders/${o.id}/deliver-driver`);
            toast.success(t("mark_delivered"));
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || t("error"));
        }
    };

    const logout = () => {
        localStorage.removeItem("c24_token");
        setUser(null);
        window.location.href = "/staff-login";
    };

    const activeMine = mine.filter((o) => o.status === "out_for_delivery");
    const finishedMine = mine.filter((o) => o.status === "paid");

    return (
        <div className="min-h-screen bg-background" dir={isAr ? "rtl" : "ltr"} data-testid="driver-home">
            {/* Header */}
            <header className="sticky top-0 z-10 bg-card/95 backdrop-blur border-b border-border px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Bike className="h-6 w-6 text-primary" />
                    <div>
                        <div className="font-medium text-sm">{t("driver_app")}</div>
                        <div className="text-xs text-muted-foreground">{user?.name}</div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        size="icon"
                        variant="ghost"
                        onClick={toggleSound}
                        title={soundOn ? (isAr ? "كتم الصوت" : "Mute") : (isAr ? "تفعيل الصوت" : "Unmute")}
                        data-testid="toggle-sound-btn"
                    >
                        {soundOn ? <Volume2 className="h-4 w-4 text-primary" /> : <VolumeX className="h-4 w-4 text-muted-foreground" />}
                    </Button>
                    <Button size="icon" variant="ghost" onClick={load} disabled={loading} data-testid="refresh-btn">
                        <RefreshCcw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={logout} data-testid="driver-logout">{t("logout")}</Button>
                </div>
            </header>

            {/* Audio unlock banner (shown once, until user taps to enable browser audio) */}
            {soundOn && !audioUnlocked && (
                <div className="bg-amber-500/15 border-b border-amber-500/30 px-4 py-2.5 flex items-center justify-between gap-2" data-testid="audio-unlock-banner">
                    <div className="flex items-center gap-2 text-xs text-amber-200 flex-1">
                        <Volume2 className="h-4 w-4 flex-shrink-0" />
                        <span>{isAr ? "اضغط لتفعيل صوت الإشعارات" : "Tap to enable notification sound"}</span>
                    </div>
                    <Button size="sm" onClick={handleUnlockAudio} className="bg-amber-500 hover:bg-amber-600 text-black h-7 px-3 text-xs" data-testid="enable-sound-btn">
                        {isAr ? "تفعيل" : "Enable"}
                    </Button>
                </div>
            )}

            {/* Tabs */}
            <div className="sticky top-[57px] z-10 bg-background border-b border-border grid grid-cols-2">
                <button
                    onClick={() => setTab("available")}
                    className={`py-3 text-sm font-medium transition-colors ${tab === "available" ? "text-primary border-b-2 border-primary" : "text-muted-foreground"}`}
                    data-testid="tab-available"
                >
                    {t("available_orders")}
                    {available.length > 0 && (
                        <span className="ms-2 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-xs font-bold">{available.length}</span>
                    )}
                </button>
                <button
                    onClick={() => setTab("mine")}
                    className={`py-3 text-sm font-medium transition-colors ${tab === "mine" ? "text-primary border-b-2 border-primary" : "text-muted-foreground"}`}
                    data-testid="tab-mine"
                >
                    {t("my_deliveries")}
                    {activeMine.length > 0 && (
                        <span className="ms-2 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-amber-500 text-black text-xs font-bold">{activeMine.length}</span>
                    )}
                </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-3 pb-20">
                {tab === "available" && (
                    <>
                        {available.length === 0 ? (
                            <div className="text-center text-muted-foreground py-16">
                                <Bike className="h-12 w-12 mx-auto mb-3 opacity-30" />
                                <p>{t("no_orders_yet")}</p>
                            </div>
                        ) : (
                            available.map((o) => (
                                <OrderCard key={o.id} o={o} lang={lang} t={t} mode="available" onPickup={handlePickup} />
                            ))
                        )}
                    </>
                )}
                {tab === "mine" && (
                    <>
                        {activeMine.length === 0 && finishedMine.length === 0 ? (
                            <div className="text-center text-muted-foreground py-16">
                                <Package className="h-12 w-12 mx-auto mb-3 opacity-30" />
                                <p>{t("no_orders_yet")}</p>
                            </div>
                        ) : (
                            <>
                                {activeMine.map((o) => (
                                    <OrderCard key={o.id} o={o} lang={lang} t={t} mode="mine" onDelivered={handleDelivered} />
                                ))}
                                {finishedMine.length > 0 && (
                                    <div className="text-xs text-muted-foreground pt-4 pb-1">
                                        {isAr ? "آخر 24 ساعة" : "Last 24 hours"} ({finishedMine.length})
                                    </div>
                                )}
                                {finishedMine.map((o) => (
                                    <OrderCard key={o.id} o={o} lang={lang} t={t} mode="mine" />
                                ))}
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
